import { KeyRound, Droplets, Lock, Unlock } from "lucide-react";
import { AddressChip, BasisBadge, Panel } from "../trace/Primitives";
import { fmtUsd, fmtPct, timeAgo } from "../../lib/trace";

const AuthRow = ({ label, value, testId, link }) => (
  <div className="flex items-center justify-between gap-3 border-t border-trace-line/60 py-3 first:border-0" data-testid={testId}>
    <span className="flex items-center gap-2 text-sm text-trace-muted">
      {value ? <Unlock size={14} className="text-red-400" /> : <Lock size={14} className="text-trace-green" />} {label}
    </span>
    {value ? <AddressChip address={value} link={link} /> : <span className="font-mono text-xs text-trace-green">Revoked</span>}
  </div>
);

export const AuthorityPanel = ({ data }) => {
  const t = data.token;
  const link = !data.is_demo;
  return (
    <Panel eyebrow="Token authorities" title="Control & permissions" icon={KeyRound} testId="authority-panel" right={<BasisBadge basis="confirmed" />}>
      <AuthRow label="Mint authority" value={t.mint_authority} testId="mint-authority-row" link={link} />
      <AuthRow label="Freeze authority" value={t.freeze_authority} testId="freeze-authority-row" link={link} />
      <div className="flex items-center justify-between border-t border-trace-line/60 py-3 text-sm" data-testid="metadata-mutable-row">
        <span className="text-trace-muted">Metadata</span>
        <span className={`font-mono text-xs ${t.metadata_mutable ? "text-amber-300" : "text-trace-green"}`}>
          {t.metadata_mutable === null || t.metadata_mutable === undefined ? "Unknown" : t.metadata_mutable ? "Mutable" : "Immutable"}
        </span>
      </div>
      <div className="flex items-center justify-between border-t border-trace-line/60 py-3 text-sm">
        <span className="text-trace-muted">Update authority</span>
        <AddressChip address={t.update_authority} link={link} />
      </div>
      <div className="border-t border-trace-line/60 pt-3 text-sm" data-testid="token-extensions">
        <span className="text-trace-muted">Token-2022 extensions</span>
        <p className="mt-1 font-mono text-xs text-white">{t.extensions.length ? t.extensions.join(", ") : t.program === "Token-2022" ? "None" : "N/A (standard SPL token)"}</p>
      </div>
    </Panel>
  );
};

export const LiquidityPanel = ({ data }) => {
  const m = data.market;
  const l = data.liquidity;
  return (
    <Panel eyebrow={`Liquidity · ${l.total_source || "unavailable"}`} title={`Liquidity ${fmtUsd(l.total_usd)}`} icon={Droplets} testId="liquidity-panel"
      className="lg:col-span-3" right={<BasisBadge basis="market" />}>
      {m.pairs?.length ? (
        <div className="overflow-x-auto">
          <table className="w-full min-w-[520px] text-left text-sm">
            <thead><tr className="eyebrow"><th className="pb-2 font-normal">DEX</th><th className="pb-2 font-normal">Pair</th><th className="pb-2 text-right font-normal">Liquidity</th><th className="pb-2 text-right font-normal">24h vol</th><th className="pb-2 text-right font-normal">Created</th></tr></thead>
            <tbody>
              {m.pairs.map((p) => (
                <tr key={p.pair_address} className="border-t border-trace-line/60" data-testid={`pair-row-${p.pair_address}`}>
                  <td className="py-2.5 capitalize text-white">{p.dex} <span className="text-trace-dim">/ {p.quote_symbol}</span></td>
                  <td className="py-2.5"><AddressChip address={p.pair_address} link={!data.is_demo} /></td>
                  <td className="py-2.5 text-right font-mono text-xs text-white">{fmtUsd(p.liquidity_usd)}</td>
                  <td className="py-2.5 text-right font-mono text-xs text-trace-muted">{fmtUsd(p.volume_h24)}</td>
                  <td className="py-2.5 text-right font-mono text-xs text-trace-muted">{timeAgo(p.created_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : <p className="text-sm text-trace-muted">No DEX pairs found.</p>}
      {l.markets.length > 0 && (
        <div className="mt-6 border-t border-trace-line pt-4">
          <p className="eyebrow mb-3">LP lock status (RugCheck)</p>
          <div className="flex flex-wrap gap-2">
            {l.markets.map((x) => (
              <span key={x.market} className="rounded-md border border-trace-line bg-trace-panel px-2.5 py-1 font-mono text-[11px] text-trace-muted">
                {x.type}: <span className={x.lp_locked_pct >= 90 ? "text-trace-green" : "text-amber-300"}>{fmtPct(x.lp_locked_pct, 0)} locked/burned</span>
              </span>
            ))}
          </div>
          <p className="mt-3 text-xs text-trace-dim">Concentrated-liquidity pools (e.g. Orca Whirlpools, Meteora DLMM) have no LP token to lock or burn, so 0% is expected for them.</p>
        </div>
      )}
    </Panel>
  );
};
